import { AxiosHttpClient } from './AxiosHttpClient';
import type { IHttpClient } from './IHttpClient';

const DEFAULT_INTERVAL = 5000; // 5 seconds
const DEFAULT_BATCH_SIZE = typeof window === 'undefined' ? 30 : 10;
const MAX_RETRIES = 3; // Maximum number of retries
const RETRY_DELAY = 2000; // Delay between retries in milliseconds

type LogLevel = 'info' | 'error' | 'debug' | 'warn';

interface IConfig {
  endpoint: string;
  serviceName: string;
  env?: 'DEV' | 'PROD';
  batchSize?: number;
  interval?: number;
  sessionId?: string;
  isWindow?: true;
  identifier?: string;
  hostName?: string;
  enabled?: boolean;
}

interface LogOptions {
  serviceName?: string;
  hostName?: string;
  identifier?: string;
  batchSize?: number;
  sessionId?: string;
  interval?: number;
  enabled?: boolean;
}

interface LogEntry {
  message: string; // Array to hold multiple message parts
  level: LogLevel;
  timestamp: number;
  attributes: any;
}

type LogPayload = {
  common: {
    attributes: {
      service: string;
      sessionId: string;
      identifier: string;
      window: boolean;
      hostname: string;
    };
  };
  logs: LogEntry[];
};

function getUUID() {
  // eslint gets funny about bitwise
  /* eslint-disable */
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const piece = (Math.random() * 16) | 0;
    const elem = c === 'x' ? piece : (piece & 0x3) | 0x8;
    return elem.toString(16);
  });
  /* eslint-enable */
}

// add identifier

class PlumLogger {
  private config: IConfig;

  private logQueue: LogEntry[] = [];

  // @ts-ignore
  private intervalId?: number;

  private httpClient: IHttpClient;

  constructor(config: IConfig) {
    this.config = {
      ...config,
      batchSize: config.batchSize ?? DEFAULT_BATCH_SIZE,
      interval: config.interval ?? DEFAULT_INTERVAL,
      sessionId: config.sessionId || getUUID(),
      isWindow: config.isWindow || true,
      identifier: config.identifier ?? config.sessionId,
      env: config.env || 'DEV',
      hostName: config.hostName,
      enabled: config.enabled,
    };
    this.httpClient = new AxiosHttpClient({
      retries: MAX_RETRIES,
    });
    if (this.config.enabled) {
      this.startInterval();
      this.attachUnloadHandler();
    }
  }

  private startInterval() {
    if (typeof window !== 'undefined') {
      this.intervalId = window.setInterval(
        () => this.sendLogs(0),
        this.config.interval
      );
    }
  }

  private attachUnloadHandler() {
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.handleUnload());
    }
  }

  private createPayload(logQueue: LogEntry[]): [LogPayload] {
    return [
      {
        common: {
          attributes: {
            service: this.config.serviceName,
            sessionId: this.config.sessionId!,
            identifier: this.config.identifier!,
            window: this.config.isWindow!,
            hostname: this.config.hostName!,
          },
        },
        logs: logQueue,
      },
    ];
  }

  private handleUnload() {
    if (this.logQueue.length > 0) {
      const data = JSON.stringify(this.createPayload(this.logQueue));
      if (navigator.sendBeacon) {
        navigator.sendBeacon(this.config.endpoint, data);
      } else {
        const client = new XMLHttpRequest();
        client.open('POST', this.config.endpoint, false); // Synchronous request
        client.setRequestHeader(
          'Content-Type',
          'application/json;charset=UTF-8'
        );
        client.send(data);
      }
    }
  }

  private async sendLogs(retryCount: number) {
    if (this.logQueue.length === 0 || retryCount > MAX_RETRIES) {
      return;
    }

    const logsToSend = this.logQueue.splice(0, this.config.batchSize);
    const logPayload = this.createPayload(logsToSend);

    const result = await this.httpClient.Post(
      this.config.endpoint,
      logPayload,
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );
    if (result.isFailure) {
      console.error('Failed to send logs, retrying...', result.error);
      setTimeout(() => this.sendLogs(retryCount + 1), RETRY_DELAY);
      // Re-add the logs to the queue for retry
      this.logQueue.unshift(...logsToSend);
    }
  }

  private log(level: LogLevel, message: string, data?: any) {
    if (!this.config.enabled) {
      return;
    }
    const logEntry: LogEntry = {
      message,
      level,
      timestamp: new Date().getTime(),
      attributes: data,
    };

    this.logQueue.push(logEntry);

    if (this.logQueue.length >= this.config.batchSize!) {
      this.sendLogs(0);
    }
  }

  updateConfig(options: LogOptions) {
    if (options.batchSize) {
      this.config.batchSize = options.batchSize;
    }
    if (options.hostName) {
      this.config.hostName = options.hostName;
    }
    if (options.identifier) {
      this.config.identifier = options.identifier;
    }
    if (options.interval) {
      this.config.interval = options.interval;
    }
    if (options.serviceName) {
      this.config.serviceName = options.serviceName;
    }
    if (options.sessionId) {
      this.config.sessionId = options.sessionId;
    }
    if (options.enabled) {
      this.config.enabled = options.enabled;
    }
  }

  info(message: string, data?: any) {
    this.log('info', message, data);
  }

  error(message: string, data?: any) {
    this.log('error', message, data);
  }

  debug(message: string, data?: any) {
    this.log('debug', message, data);
  }

  warn(message: string, data?: any) {
    this.log('warn', message, data);
  }

  public async flushLogsAndAwait(): Promise<void> {
    await this.sendLogs(0);
  }
}

export default PlumLogger;
